import { Clock, Flower2, Heart, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";

import { Layout } from "@/components/layout";

export function Spa() {
  const treatments = [
    {
      category: "Signature Massages",
      services: [
        {
          name: "Thai Traditional Massage",
          duration: "90 min",
          description: "Ancient healing therapy combining acupressure and assisted yoga stretches",
        },
        {
          name: "Amor Signature Massage",
          duration: "120 min",
          description: "Bespoke treatment blending Thai and Swedish techniques with aromatic oils",
        },
        {
          name: "Hot Stone Therapy",
          duration: "90 min",
          description: "Deep relaxation using heated volcanic stones and therapeutic massage",
        },
        {
          name: "Aromatherapy Massage",
          duration: "60 min",
          description: "Customized massage with essential oils to restore balance and harmony",
        },
      ],
    },
    {
      category: "Facial Treatments",
      services: [
        {
          name: "Tropical Radiance Facial",
          duration: "75 min",
          description: "Rejuvenating facial using local fruits and botanicals for glowing skin",
        },
        {
          name: "Anti-Aging Treatment",
          duration: "90 min",
          description: "Advanced facial therapy targeting fine lines and promoting skin renewal",
        },
        {
          name: "Hydrating Coconut Facial",
          duration: "60 min",
          description: "Deeply moisturizing treatment with pure coconut and aloe vera",
        },
      ],
    },
    {
      category: "Body Treatments",
      services: [
        {
          name: "Coconut Body Scrub",
          duration: "60 min",
          description: "Exfoliating treatment leaving skin silky smooth and nourished",
        },
        {
          name: "Seaweed Body Wrap",
          duration: "90 min",
          description: "Detoxifying wrap with mineral-rich seaweed and essential oils",
        },
        {
          name: "Thai Herbal Compress",
          duration: "75 min",
          description: "Traditional therapy using heated herbal bundles to ease tension",
        },
      ],
    },
  ];

  const facilities = [
    {
      icon: Sparkles,
      name: "Treatment Rooms",
      description: "Six private spa suites with ocean views",
    },
    {
      icon: Flower2,
      name: "Relaxation Garden",
      description: "Tranquil outdoor space for pre and post-treatment",
    },
    {
      icon: Heart,
      name: "Couples Suite",
      description: "Intimate space for shared wellness experiences",
    },
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative h-[60vh] min-h-[400px]">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url(https://images.pexels.com/photos/5266394/pexels-photo-5266394.jpeg?auto=compress&cs=tinysrgb&w=1920)",
          }}
        >
          <div className="absolute inset-0 bg-black/40" />
        </div>

        <div className="relative h-full flex items-center justify-center text-center px-6">
          <div className="max-w-3xl">
            <h1 className="font-heading text-5xl md:text-6xl font-bold text-white mb-4 tracking-tight">
              Spa & Wellness
            </h1>
            <p className="text-xl text-white/95 font-light tracking-wide">
              A sanctuary for body, mind, and spirit
            </p>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-stone-900 mb-6">
            The Amor Spa Experience
          </h2>
          <div className="w-24 h-px bg-luxury-gold mx-auto mb-8" />
          <p className="text-lg text-stone-700 leading-relaxed mb-6">
            Inspired by ancient Thai healing traditions and enhanced with contemporary wellness
            practices, our spa offers a holistic approach to rejuvenation. Each treatment is
            thoughtfully designed to restore balance, relieve tension, and awaken your senses.
          </p>
          <p className="text-lg text-stone-700 leading-relaxed">
            Our skilled therapists use only the finest natural ingredients sourced from Thailand's
            tropical bounty - coconut, lemongrass, Thai herbs, and aromatic essential oils.
          </p>
        </div>
      </section>

      {/* Spa Philosophy with Image */}
      <section className="py-16 px-6 bg-stone-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative h-[450px]">
              <img
                src="https://images.pexels.com/photos/3997993/pexels-photo-3997993.jpeg?auto=compress&cs=tinysrgb&w=800"
                alt="Spa Treatment"
                className="w-full h-full object-cover"
              />
            </div>

            <div>
              <h3 className="font-heading text-3xl font-bold text-stone-900 mb-6">
                Our Philosophy
              </h3>
              <p className="text-stone-700 leading-relaxed mb-6">
                At The Amor Spa, we believe true wellness comes from harmony between body and mind.
                Our treatments honor the ancient wisdom of Thai healing while incorporating modern
                techniques to meet the needs of today's traveler.
              </p>
              <p className="text-stone-700 leading-relaxed mb-8">
                Every journey begins with a personal consultation to understand your unique needs,
                ensuring each treatment is perfectly tailored to you.
              </p>

              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-2 h-2 bg-luxury-gold rounded-full mt-2 flex-shrink-0" />
                  <p className="text-stone-700">
                    <strong>Natural Ingredients:</strong> Organic oils, herbs, and botanicals from
                    Thailand
                  </p>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-2 h-2 bg-luxury-gold rounded-full mt-2 flex-shrink-0" />
                  <p className="text-stone-700">
                    <strong>Expert Therapists:</strong> Trained in traditional and contemporary
                    techniques
                  </p>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-2 h-2 bg-luxury-gold rounded-full mt-2 flex-shrink-0" />
                  <p className="text-stone-700">
                    <strong>Personalized Care:</strong> Treatments customized to your individual
                    needs
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Treatment Menu */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-stone-900 text-center mb-4">
            Treatment Menu
          </h2>
          <div className="w-24 h-px bg-luxury-gold mx-auto mb-12" />

          <div className="space-y-12">
            {treatments.map((category, idx) => (
              <div key={idx}>
                <h3 className="font-heading text-2xl font-bold text-stone-900 mb-6 pb-3 border-b-2 border-luxury-gold inline-block">
                  {category.category}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                  {category.services.map((service, index) => (
                    <div
                      key={index}
                      className="border border-stone-200 p-6 hover:shadow-lg transition-shadow"
                    >
                      <div className="flex justify-between items-start mb-3">
                        <h4 className="font-heading text-lg font-bold text-stone-900">
                          {service.name}
                        </h4>
                        <div className="flex items-center gap-1 text-stone-500 text-sm">
                          <Clock size={14} />
                          <span>{service.duration}</span>
                        </div>
                      </div>
                      <p className="text-stone-600 text-sm leading-relaxed">
                        {service.description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Facilities */}
      <section className="py-16 px-6 bg-stone-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-stone-900 text-center mb-12">
            Spa Facilities
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {facilities.map((facility, index) => {
              const Icon = facility.icon;
              return (
                <div key={index} className="bg-white p-8 border border-stone-200 text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-stone-900 text-white mb-4">
                    <Icon size={28} />
                  </div>
                  <h3 className="font-heading text-xl font-bold text-stone-900 mb-2">
                    {facility.name}
                  </h3>
                  <p className="text-stone-600 leading-relaxed">{facility.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Wellness Image Section */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="font-heading text-3xl font-bold text-stone-900 mb-6">
                Wellness Activities
              </h3>
              <p className="text-stone-700 leading-relaxed mb-6">
                Complement your spa treatments with our daily wellness offerings designed to enhance
                your overall well-being during your stay.
              </p>

              <div className="space-y-4">
                <div className="border-l-2 border-luxury-gold pl-4">
                  <h4 className="font-semibold text-stone-900 mb-1">Beach Yoga</h4>
                  <p className="text-sm text-stone-600">
                    Daily sunrise and sunset sessions on the beach
                  </p>
                </div>
                <div className="border-l-2 border-luxury-gold pl-4">
                  <h4 className="font-semibold text-stone-900 mb-1">Meditation Garden</h4>
                  <p className="text-sm text-stone-600">
                    Peaceful space for mindfulness and reflection
                  </p>
                </div>
                <div className="border-l-2 border-luxury-gold pl-4">
                  <h4 className="font-semibold text-stone-900 mb-1">Fitness Classes</h4>
                  <p className="text-sm text-stone-600">
                    Personal training and group classes available
                  </p>
                </div>
              </div>
            </div>

            <div className="relative h-[400px]">
              <img
                src="https://images.pexels.com/photos/458541/pexels-photo-458541.jpeg?auto=compress&cs=tinysrgb&w=800"
                alt="Wellness"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Spa Hours & Booking */}
      <section className="py-16 px-6 bg-stone-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
            Book Your Spa Experience
          </h2>
          <div className="w-24 h-px bg-luxury-gold mx-auto mb-8" />

          <div className="flex items-center justify-center gap-2 text-stone-300 mb-8">
            <Clock size={20} />
            <span className="text-lg">Open Daily: 9:00 AM - 9:00 PM</span>
          </div>

          <p className="text-lg text-stone-300 mb-8 leading-relaxed">
            Advance booking is recommended to secure your preferred treatment time. Our spa team is
            available to create a personalized wellness program for your stay.
          </p>

          <Link
            to="/contact"
            className="bg-white text-stone-900 px-10 py-4 text-sm tracking-widest uppercase font-semibold hover:bg-stone-100 transition-colors inline-block"
          >
            Schedule Appointment
          </Link>
        </div>
      </section>
    </Layout>
  );
}
